/**
 * /slides/:slideId — 3-pane 편집기 (사이드바 없는 풀 폭 layout).
 *
 * 좌측: SlideChatPanel — slide-creator 스킬과 1:1 바인딩된 채팅
 * 중앙: SlideCanvas + 썸네일 레일 — 1920×1080 페이지 미리보기
 * 우측: PageInspector + 자산 패널
 *
 * 자산 업로드: image 블록의 assetId 는 [GET /api/slides/:id/assets/:aid] 로 직접 stream.
 * PDF: 클라이언트 사이드 window.print() — 모든 페이지를 hidden 1920×1080 컨테이너에 마운트.
 */
import { BatchQuestionnaire } from '@/components/slides/BatchQuestionnaire';
import { DesignPanel } from '@/components/slides/DesignPanel';
import { PageInspector } from '@/components/slides/PageInspector';
import { PageRenderer } from '@/components/slides/PageRenderer';
import { SlideCanvas } from '@/components/slides/SlideCanvas';
import { SlideChatPanel } from '@/components/slides/SlideChatPanel';
import { ThemeGallery } from '@/components/slides/ThemeGallery';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { printSlides } from '@/lib/slides/export-pdf';
import { exportPptx } from '@/lib/slides/export-pptx';
import type { SlideAsset } from '@innermate/shared';
import type { InterruptPrompt, InterruptResolveRequest } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Download,
  FileBox,
  Play,
  Plus,
  Star,
  StarOff,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  slideId: string;
}

export function SlidesEditor({ slideId }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null);
  const [printing, setPrinting] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  const [activeBatchInterrupt, setActiveBatchInterrupt] = useState<{
    interruptId: string;
    prompt: InterruptPrompt;
  } | null>(null);
  const [externalResolve, setExternalResolve] = useState<{
    interruptId: string;
    body: InterruptResolveRequest;
  } | null>(null);
  const printRootRef = useRef<HTMLDivElement | null>(null);

  const slideQuery = useQuery({
    queryKey: ['slide', slideId],
    queryFn: () => api.slideGet(slideId),
    staleTime: 1_000,
  });
  const assetsQuery = useQuery({
    queryKey: ['slide-assets', slideId],
    queryFn: () => api.slideAssets(slideId),
    staleTime: 1_000,
  });

  useEffect(() => {
    const pages = slideQuery.data?.pages ?? [];
    if (pages.length === 0) {
      if (selectedPageId !== null) setSelectedPageId(null);
      return;
    }
    const stillExists = pages.some((p) => p.id === selectedPageId);
    if (!stillExists) setSelectedPageId(pages[0]?.id ?? null);
  }, [slideQuery.data, selectedPageId]);

  const favMut = useMutation({
    mutationFn: (isFavorite: boolean) => api.slideFavorite(slideId, { isFavorite }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slide', slideId] }),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.slideDelete(slideId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slides', 'list'] });
      navigate({ to: '/slides' });
    },
  });
  const addPageMut = useMutation({
    mutationFn: () => {
      const pages = slideQuery.data?.pages ?? [];
      const last = pages[pages.length - 1];
      return api.slideCreatePage(slideId, {
        afterPageId: last?.id,
        content: {
          mode: 'blocks',
          layout: 'title-body',
          blocks: [
            { type: 'heading', level: 'section', text: '새 페이지', align: 'left' },
            { type: 'body', text: '내용을 입력하세요.', size: 'md', align: 'left' },
          ],
        },
      });
    },
    onSuccess: (page) => {
      qc.invalidateQueries({ queryKey: ['slide', slideId] });
      setSelectedPageId(page.id);
    },
  });

  const assetUrlMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of assetsQuery.data?.assets ?? []) m.set(a.id, a.url);
    return m;
  }, [assetsQuery.data]);
  const resolveAssetUrl = (id: string) => assetUrlMap.get(id);

  const handlePrint = async () => {
    const root = printRootRef.current;
    if (!root) return;
    setPrinting(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => r(undefined)));
      await printSlides({ printRoot: root });
    } finally {
      setPrinting(false);
    }
  };

  const handleExportPptx = async () => {
    const root = printRootRef.current;
    if (!root) return;
    setExportingPptx(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => r(undefined)));
      const slide = slideQuery.data;
      if (!slide) return;
      const pageNodes: { node: HTMLElement; notes?: string | null }[] = [];
      const items = root.querySelectorAll<HTMLElement>('[data-innermate-print-page]');
      items.forEach((el, i) => {
        const p = slide.pages[i];
        pageNodes.push({ node: el, notes: p?.notes ?? null });
      });
      await exportPptx(pageNodes, { title: slide.title });
    } catch (err) {
      alert(`PPTX 내보내기 실패: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExportingPptx(false);
    }
  };

  const handlePresent = () => {
    navigate({ to: '/slides/$slideId/present', params: { slideId } });
  };

  if (slideQuery.isLoading) {
    return (
      <div className="h-[100dvh] grid place-items-center text-sm text-foreground-muted">
        슬라이드 불러오는 중…
      </div>
    );
  }
  if (slideQuery.isError || !slideQuery.data) {
    return (
      <div className="h-[100dvh] grid place-items-center text-sm text-destructive">
        슬라이드를 찾을 수 없습니다.
      </div>
    );
  }

  const slide = slideQuery.data;
  const selectedPage = slide.pages.find((p) => p.id === selectedPageId) ?? slide.pages[0] ?? null;
  const sessionId = slide.chatSessionId;

  return (
    <div className="h-[100dvh] flex flex-col bg-background text-foreground overflow-hidden">
      <header className="flex items-center gap-2 px-3 h-11 border-b border-border bg-background shrink-0">
        <Link
          to="/slides"
          className="-ml-1 inline-flex items-center gap-2 px-2 h-8 rounded-md text-sm text-foreground-muted hover:bg-surface-muted/60 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-4" />
          슬라이드 목록
        </Link>
        <div className="w-px h-5 bg-border mx-1" />
        <h1 className="text-sm font-medium truncate">{slide.title}</h1>
        <div className="flex-1" />
        <span className="text-xs text-foreground-muted">{slide.pages.length}장</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePresent}
          disabled={slide.pages.length === 0}
          aria-label="발표 모드"
        >
          <Play className="size-4" />
          발표
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handlePrint}
          disabled={printing || slide.pages.length === 0}
          aria-label="PDF 로 인쇄"
        >
          <Download className="size-4" />
          PDF
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExportPptx}
          disabled={exportingPptx || slide.pages.length === 0}
          aria-label="PPTX 로 내보내기"
        >
          <FileBox className="size-4" />
          {exportingPptx ? 'PPTX…' : 'PPTX'}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={slide.isFavorite ? '즐겨찾기 해제' : '즐겨찾기'}
          onClick={() => favMut.mutate(!slide.isFavorite)}
        >
          {slide.isFavorite ? (
            <Star className="size-4 fill-current text-yellow-500" />
          ) : (
            <StarOff className="size-4" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label="삭제"
          onClick={() => {
            if (confirm('이 슬라이드를 삭제하시겠어요?')) deleteMut.mutate();
          }}
        >
          <Trash2 className="size-4" />
        </Button>
      </header>

      <div className="flex-1 grid grid-cols-[360px_1fr_300px] min-h-0">
        {/* 좌측 — 채팅 */}
        <aside className="border-r border-border bg-background flex flex-col min-h-0">
          {sessionId ? (
            <SlideChatPanel
              sessionId={sessionId}
              slideId={slideId}
              onActiveInterruptChange={(payload) => {
                if (!payload) {
                  setActiveBatchInterrupt(null);
                  return;
                }
                if (payload.prompt.kind === 'batch_ask_user') {
                  setActiveBatchInterrupt(payload);
                } else {
                  setActiveBatchInterrupt(null);
                }
              }}
              externalResolve={externalResolve}
              onExternalResolveConsumed={() => setExternalResolve(null)}
            />
          ) : (
            <div className="p-4 text-sm text-foreground-muted">
              이 슬라이드에 채팅 세션이 연결되어 있지 않아요.
            </div>
          )}
        </aside>

        {/* 중앙 — 캔버스 + 썸네일 레일 */}
        <section className="flex flex-col min-h-0 bg-surface-muted/40">
          <div className="flex-1 min-h-0 relative">
            {/*
             * 우선순위:
             *  (1) 모델이 system.batch_ask_user 호출 중 — 캔버스 자리에 BatchQuestionnaire (동적 질문)
             *  (2) 페이지 있음 — SlideCanvas + PageRenderer
             *  (3) 페이지 0 + 인터럽트 없음 — InitialQuestionnaire (정적 빠른 시작 폼)
             */}
            {activeBatchInterrupt && activeBatchInterrupt.prompt.kind === 'batch_ask_user' ? (
              <BatchQuestionnaire
                title={activeBatchInterrupt.prompt.title}
                prompt={activeBatchInterrupt.prompt.prompt}
                questions={activeBatchInterrupt.prompt.questions ?? []}
                allowSkip={activeBatchInterrupt.prompt.allowSkip}
                pending={externalResolve != null}
                onSubmit={(body) =>
                  setExternalResolve({ interruptId: activeBatchInterrupt.interruptId, body })
                }
              />
            ) : selectedPage ? (
              <div className="absolute inset-0 p-4" onClick={() => setSelectedBlockIndex(null)}>
                <SlideCanvas design={slide.design}>
                  <PageRenderer
                    page={selectedPage}
                    resolveAssetUrl={resolveAssetUrl}
                    design={slide.design}
                    assets={assetsQuery.data?.assets ?? []}
                    onSelectBlock={(idx) => setSelectedBlockIndex(idx)}
                    selectedBlockIndex={selectedBlockIndex}
                  />
                </SlideCanvas>
              </div>
            ) : (
              // 페이지 0개 + 인터럽트 없음 — 자연스러운 인사. 사용자 메시지 보내면 모델이 batch_ask_user 호출.
              <div className="absolute inset-0 grid place-items-center p-8">
                <div className="max-w-md text-center">
                  <h2 className="text-2xl font-semibold tracking-tight">
                    어떤 슬라이드를 만들어 볼까요?
                  </h2>
                  <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
                    좌측 채팅에 발표 주제를 자유롭게 적어주세요.
                    <br />몇 가지 더 여쭤본 뒤 첫 페이지부터 그려드릴게요.
                  </p>
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-border bg-background p-2 flex gap-2 overflow-x-auto items-center shrink-0 select-none">
            {slide.pages.map((p, i) => {
              const active = p.id === selectedPage?.id;
              return (
                <button
                  type="button"
                  key={p.id}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  onClick={() => {
                    setSelectedPageId(p.id);
                    setSelectedBlockIndex(null);
                  }}
                  aria-label={`페이지 ${i + 1}`}
                  title={`페이지 ${i + 1}`}
                  className={`relative shrink-0 rounded-md transition-shadow overflow-hidden bg-white ${
                    active
                      ? 'shadow-[0_0_0_2px_var(--accent)] hover:shadow-[0_0_0_2px_var(--accent)]'
                      : 'shadow-[inset_0_0_0_1px_var(--color-border,rgba(0,0,0,0.1))] hover:shadow-[inset_0_0_0_2px_var(--color-foreground-muted,rgba(0,0,0,0.4))]'
                  }`}
                  style={
                    {
                      width: 144,
                      height: 81,
                      WebkitUserDrag: 'none',
                      userSelect: 'none',
                    } as React.CSSProperties
                  }
                >
                  {/* 자식 렌더는 read-only — 클릭이 button onClick 으로만 일관 전달. */}
                  <div style={{ pointerEvents: 'none', width: '100%', height: '100%' }}>
                    <SlideCanvas design={slide.design}>
                      <PageRenderer
                        page={p}
                        resolveAssetUrl={resolveAssetUrl}
                        design={slide.design}
                        assets={assetsQuery.data?.assets ?? []}
                      />
                    </SlideCanvas>
                  </div>
                  <span
                    aria-hidden
                    className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-mono leading-none ${
                      active
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-foreground/70 text-background opacity-0 group-hover:opacity-100'
                    }`}
                  >
                    {i + 1}
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => addPageMut.mutate()}
              aria-label="페이지 추가"
              className="shrink-0 rounded-md border-2 border-dashed border-border text-foreground-muted hover:border-foreground hover:text-foreground transition-colors grid place-items-center"
              style={{ width: 144, height: 81 }}
              disabled={addPageMut.isPending}
            >
              <Plus className="size-5" />
            </button>
          </div>
        </section>

        {/* 우측 — 테마 + 페이지 속성 + 자산 */}
        <aside className="border-l border-border bg-background min-h-0 overflow-y-auto flex flex-col">
          <div className="p-4 border-b border-border space-y-4">
            <ThemeGallery slideId={slideId} currentDesign={slide.design} />
            <details className="group">
              <summary className="text-[11px] uppercase tracking-wide text-foreground-muted cursor-pointer hover:text-foreground select-none flex items-center justify-between">
                <span>직접 조정</span>
                <span className="text-[10px] group-open:hidden">열기</span>
                <span className="text-[10px] hidden group-open:inline">접기</span>
              </summary>
              <div className="pt-3">
                <DesignPanel slideId={slideId} design={slide.design} />
              </div>
            </details>
          </div>
          <div className="p-4 border-b border-border">
            <div className="text-xs uppercase tracking-wide text-foreground-muted mb-3">
              페이지 속성
            </div>
            {selectedPage ? (
              <PageInspector
                slideId={slideId}
                page={selectedPage}
                assets={assetsQuery.data?.assets ?? []}
                selectedBlockIndex={selectedBlockIndex}
              />
            ) : (
              <div className="text-xs text-foreground-muted">선택된 페이지가 없어요.</div>
            )}
          </div>
          <AssetsPanel
            slideId={slideId}
            assets={assetsQuery.data?.assets ?? []}
            isLoading={assetsQuery.isLoading}
          />
        </aside>
      </div>

      {/* PDF 인쇄용 hidden 컨테이너 — printSlides 가 @media print 로 표시. */}
      {createPortal(
        <div
          ref={printRootRef}
          aria-hidden
          style={{ position: 'fixed', left: '-99999px', top: 0, pointerEvents: 'none' }}
        >
          {slide.pages.map((p) => (
            <div key={p.id} data-innermate-print-page>
              <SlideCanvas design={slide.design} scale={1} center={false}>
                <PageRenderer
                  page={p}
                  resolveAssetUrl={resolveAssetUrl}
                  design={slide.design}
                  assets={assetsQuery.data?.assets ?? []}
                />
              </SlideCanvas>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

function AssetsPanel({
  slideId,
  assets,
  isLoading,
}: {
  slideId: string;
  assets: SlideAsset[];
  isLoading: boolean;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadMut = useMutation({
    mutationFn: (file: File) => api.slideUploadAsset(slideId, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['slide-assets', slideId] });
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : '업로드 실패'),
  });
  const deleteMut = useMutation({
    mutationFn: (assetId: string) => api.slideDeleteAsset(slideId, assetId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slide-assets', slideId] }),
  });

  const onFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const f of Array.from(files)) {
      uploadMut.mutate(f);
    }
  };

  return (
    <div className="p-4 flex-1">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wide text-foreground-muted">이미지 자산</div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => inputRef.current?.click()}
          disabled={uploadMut.isPending}
        >
          <Upload className="size-3.5" />
          {uploadMut.isPending ? '업로드 중…' : '추가'}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
          multiple
          className="hidden"
          onChange={(e) => {
            onFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {error && (
        <div className="mb-3 px-2 py-1.5 rounded-md bg-destructive/10 text-destructive text-xs">
          {error}
        </div>
      )}
      {isLoading ? (
        <p className="text-xs text-foreground-muted">불러오는 중…</p>
      ) : assets.length === 0 ? (
        <p className="text-xs text-foreground-muted leading-relaxed">
          아직 업로드한 이미지가 없어요. 채팅에서 모델이 image 블록을 쓰려면 먼저 자산이 필요합니다.
        </p>
      ) : (
        <ul className="space-y-2">
          {assets.map((a) => (
            <li
              key={a.id}
              className="group relative rounded-md border border-border overflow-hidden"
            >
              <img
                src={a.url}
                alt={a.filename}
                className="w-full h-24 object-cover bg-surface-muted"
                loading="lazy"
              />
              <div className="px-2 py-1.5 flex items-center gap-2">
                <span className="text-[11px] truncate flex-1" title={a.filename}>
                  {a.filename}
                </span>
                <button
                  type="button"
                  onClick={() => navigator.clipboard.writeText(a.id)}
                  className="text-[10px] text-foreground-muted hover:text-foreground"
                  title="assetId 복사"
                >
                  ID 복사
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('자산을 삭제하시겠어요? 사용 중인 image 블록은 끊깁니다.')) {
                      deleteMut.mutate(a.id);
                    }
                  }}
                  aria-label="삭제"
                  className="text-foreground-muted hover:text-destructive"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
